import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Gavel, Plus, Trash2, Pencil, Check, X, AlertCircle, Building2, Clock } from "lucide-react";

const TIERS = [
  { value: "discepolo", label: "Discepolo" },
  { value: "custode", label: "Custode" },
  { value: "ambasciatore", label: "Ambasciatore" },
];

const FORMATS = ["0.75L", "1.5L Magnum", "3L Jeroboam", "5L Jeroboam Bordeaux", "6L Methuselah", "9L Salmanazar", "12L Balthazar"];

const eur = (n) => `€${Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

const isoFromInput = (v) => v ? new Date(v).toISOString() : "";

function AuctionForm({ initial, onSave, onCancel }) {
  const futureDefault = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 16);
  const [form, setForm] = useState({
    lot_name: "", producer: "", description: "", format: "0.75L", bottles: 1,
    vintage: new Date().getFullYear() - 5, region: "", image: "",
    starting_bid: 1000, estimate_min: 1500, estimate_max: 3000, provenance: "",
    tier_required: "discepolo", ends_at: futureDefault,
    ...initial,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.estimate_max) < Number(form.estimate_min)) { toast.error("estimate_max ≥ estimate_min"); return; }
    if (Number(form.estimate_min) < Number(form.starting_bid)) { toast.error("estimate_min ≥ starting_bid"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        bottles: Number(form.bottles),
        vintage: form.vintage ? Number(form.vintage) : null,
        starting_bid: Number(form.starting_bid),
        estimate_min: Number(form.estimate_min),
        estimate_max: Number(form.estimate_max),
        ends_at: form.ends_at.length === 16 ? isoFromInput(form.ends_at) : form.ends_at,
      };
      await onSave(payload);
      toast.success(initial?.id ? "Asta aggiornata" : "Asta creata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <form data-testid="auction-form" onSubmit={submit} className="bg-card border border-border rounded-sm p-6 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Nome Lotto *</label>
          <Input data-testid="auc-lot" required value={form.lot_name} onChange={(e) => setForm({ ...form, lot_name: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Produttore *</label>
          <Input data-testid="auc-producer" required value={form.producer} onChange={(e) => setForm({ ...form, producer: e.target.value })} className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold">Descrizione *</label>
          <Textarea data-testid="auc-description" required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Formato</label>
          <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Bottiglie</label>
          <Input data-testid="auc-bottles" type="number" min="1" value={form.bottles} onChange={(e) => setForm({ ...form, bottles: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Annata</label>
          <Input type="number" min="1900" max={new Date().getFullYear()} value={form.vintage || ""} onChange={(e) => setForm({ ...form, vintage: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Regione</label>
          <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Es. Toscana" className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold">URL Immagine</label>
          <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://... oppure /api/static/wines/..." className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Base d'asta (€) *</label>
          <Input data-testid="auc-starting" required type="number" min="1" value={form.starting_bid} onChange={(e) => setForm({ ...form, starting_bid: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Tier richiesto</label>
          <Select value={form.tier_required} onValueChange={(v) => setForm({ ...form, tier_required: v })}>
            <SelectTrigger data-testid="auc-tier" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Stima min (€) *</label>
          <Input required type="number" min="1" value={form.estimate_min} onChange={(e) => setForm({ ...form, estimate_min: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Stima max (€) *</label>
          <Input required type="number" min="1" value={form.estimate_max} onChange={(e) => setForm({ ...form, estimate_max: e.target.value })} className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold">Provenance</label>
          <Input value={form.provenance} onChange={(e) => setForm({ ...form, provenance: e.target.value })} placeholder="Cantina del produttore · Cassetta originale · Etichetta perfetta" className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold">Chiusura asta *</label>
          <Input data-testid="auc-ends" required type="datetime-local" value={form.ends_at?.slice(0, 16)} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="mt-1" />
        </div>
      </div>
      <div className="flex gap-2 pt-3 border-t border-border">
        <Button data-testid="auc-save" type="submit" disabled={saving} className="bg-[hsl(var(--primary))]">
          {saving ? "Salvo..." : initial?.id ? "Salva modifiche" : "Crea asta"}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Annulla</Button>
      </div>
    </form>
  );
}

function AuctionsTab() {
  const [auctions, setAuctions] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | object

  const reload = () => api.get("/club/auctions").then((r) => setAuctions(r.data || []));
  useEffect(() => { reload(); }, []);

  const create = async (payload) => {
    await api.post("/admin/auctions", payload);
    setEditing(null);
    reload();
  };
  const update = async (payload) => {
    await api.put(`/admin/auctions/${editing.id}`, payload);
    setEditing(null);
    reload();
  };
  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.lot_name}"?${a.bid_count > 0 ? "\n(Ha offerte: verrà chiusa, non eliminata)" : ""}`)) return;
    try { await api.delete(`/admin/auctions/${a.id}`); toast.success("Eliminata/chiusa"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (editing === "new") return <AuctionForm onSave={create} onCancel={() => setEditing(null)} />;
  if (editing && editing.id) return <AuctionForm initial={editing} onSave={update} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{auctions.length} aste totali</div>
        <Button data-testid="auc-new-btn" onClick={() => setEditing("new")} className="bg-[hsl(var(--primary))] gap-2"><Plus className="h-4 w-4" />Nuova asta</Button>
      </div>
      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Lotto</TableHead><TableHead>Tier</TableHead>
              <TableHead>Base</TableHead><TableHead>Offerta</TableHead>
              <TableHead>Stima</TableHead><TableHead>Chiusura</TableHead>
              <TableHead>Stato</TableHead><TableHead>Origine</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {auctions.map((a) => (
              <TableRow key={a.id} data-testid={`auc-row-${a.id}`}>
                <TableCell>
                  <div className="font-semibold">{a.lot_name}</div>
                  <div className="text-xs text-muted-foreground">{a.producer} · {a.format}{a.vintage ? ` · ${a.vintage}` : ""}</div>
                </TableCell>
                <TableCell><span className="text-[10px] uppercase px-2 py-0.5 bg-muted rounded-sm">{a.tier_required}</span></TableCell>
                <TableCell className="font-bold text-[#C9A961]">{eur(a.starting_bid)}</TableCell>
                <TableCell>{a.current_bid ? <span className="font-bold text-[#C9A961]">{eur(a.current_bid)}<div className="text-[10px] text-muted-foreground">{a.bid_count} offerte</div></span> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-xs">{eur(a.estimate_min)}–{eur(a.estimate_max)}</TableCell>
                <TableCell className="text-xs">{new Date(a.ends_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</TableCell>
                <TableCell><span className={`text-[10px] uppercase px-2 py-0.5 rounded-sm ${a.status === "open" ? "bg-emerald-500/15 text-emerald-500" : "bg-muted"}`}>{a.status || "open"}</span></TableCell>
                <TableCell>
                  {a.source === "proposal" ? (
                    <span className="text-[10px] flex items-center gap-1 text-purple-500" title={a.proposer_company || a.proposer_email}><Building2 className="h-3 w-3" />B2B</span>
                  ) : <span className="text-[10px] text-muted-foreground">Admin</span>}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button data-testid={`auc-edit-${a.id}`} onClick={() => setEditing(a)} className="p-1.5 text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`auc-del-${a.id}`} onClick={() => remove(a)} className="p-1.5 text-destructive hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {auctions.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Nessuna asta. Crea la prima.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ProposalDecisionModal({ proposal, onClose, onDone }) {
  const [override, setOverride] = useState({ starting_bid: "", ends_at: "", tier_required: proposal.tier_required });
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (decision) => {
    if (decision === "reject" && !window.confirm("Rifiutare la proposta?")) return;
    setBusy(true);
    try {
      const body = { decision, admin_notes: notes };
      if (decision === "approve") {
        if (override.starting_bid) body.override_starting_bid = Number(override.starting_bid);
        if (override.ends_at) body.override_ends_at = new Date(override.ends_at).toISOString();
        if (override.tier_required && override.tier_required !== proposal.tier_required) body.override_tier_required = override.tier_required;
      }
      await api.post(`/admin/auction-proposals/${proposal.id}/decision`, body);
      toast.success(decision === "approve" ? "Proposta approvata · asta creata" : "Proposta rifiutata");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="proposal-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-background max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-border rounded-sm">
        <div className="p-6 border-b border-border flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))]">Proposta da · {proposal.proposer_company || proposal.proposer_email}</div>
            <h2 className="editorial-heading text-2xl mt-1">{proposal.lot_name}</h2>
            <div className="text-sm text-muted-foreground">{proposal.producer} · {proposal.format} · {proposal.bottles} btl{proposal.vintage ? ` · ${proposal.vintage}` : ""}</div>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-muted p-3 rounded-sm text-sm whitespace-pre-wrap">{proposal.description}</div>

          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><span className="text-muted-foreground text-xs">Base</span><div className="font-bold text-[#C9A961]">{eur(proposal.starting_bid)}</div></div>
            <div><span className="text-muted-foreground text-xs">Stima</span><div>{eur(proposal.estimate_min)}–{eur(proposal.estimate_max)}</div></div>
            <div><span className="text-muted-foreground text-xs">Durata</span><div>{proposal.suggested_duration_days}g</div></div>
          </div>

          {proposal.proposer_notes && (
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Note del proponente</div>
              <div className="bg-muted p-3 rounded-sm text-sm italic">{proposal.proposer_notes}</div>
            </div>
          )}

          <div className="border-t border-border pt-4 space-y-3">
            <div className="text-xs uppercase tracking-wider font-semibold">Override (opzionali per approvazione)</div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Base d'asta override</label>
                <Input type="number" placeholder={String(proposal.starting_bid)} value={override.starting_bid} onChange={(e) => setOverride({ ...override, starting_bid: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Chiusura (custom)</label>
                <Input type="datetime-local" value={override.ends_at} onChange={(e) => setOverride({ ...override, ends_at: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Tier</label>
                <Select value={override.tier_required} onValueChange={(v) => setOverride({ ...override, tier_required: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold">Note admin (visibili al proponente)</label>
              <Textarea data-testid="prop-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Es. Approvata, ho alzato la base ad €X dato il prestigio del lotto..." className="mt-1" />
            </div>
            <div className="flex gap-2 pt-2 border-t border-border">
              <Button data-testid="prop-approve" onClick={() => decide("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"><Check className="h-4 w-4" />Approva e pubblica</Button>
              <Button data-testid="prop-reject" onClick={() => decide("reject")} disabled={busy} variant="outline" className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white gap-2"><X className="h-4 w-4" />Rifiuta</Button>
              <Button onClick={onClose} variant="ghost">Chiudi</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProposalsTab() {
  const [data, setData] = useState({ proposals: [], total: 0, by_status: {} });
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null);

  const reload = () => api.get("/admin/auction-proposals").then((r) => setData(r.data));
  useEffect(() => { reload(); }, []);

  const filtered = data.proposals.filter((p) => filter === "all" ? true : p.status === filter);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: "all", label: "Tutte", count: data.total, cls: "bg-muted" },
          { key: "pending", label: "In attesa", count: data.by_status.pending || 0, cls: "bg-amber-500/15 text-amber-500 border-amber-500/30 border" },
          { key: "approved", label: "Approvate", count: data.by_status.approved || 0, cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30 border" },
          { key: "rejected", label: "Rifiutate", count: data.by_status.rejected || 0, cls: "bg-red-500/15 text-red-500 border-red-500/30 border" },
        ].map((s) => (
          <button key={s.key} data-testid={`prop-flt-${s.key}`} onClick={() => setFilter(s.key)} className={`text-left p-3 rounded-sm transition-all ${filter === s.key ? s.cls : "bg-card border border-border"}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-70">{s.label}</div>
            <div className="text-2xl font-bold mt-0.5">{s.count}</div>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Gavel className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <div className="text-lg font-semibold">Nessuna proposta {filter !== "all" ? `${filter}` : ""}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <button key={p.id} data-testid={`prop-row-${p.id}`} onClick={() => setSelected(p)} className="w-full text-left bg-card border border-border hover:border-[hsl(var(--primary))]/40 rounded-sm p-4 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-semibold text-muted-foreground">{p.proposer_company || p.proposer_email}</span>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-sm ${p.status === "pending" ? "bg-amber-500/15 text-amber-500" : p.status === "approved" ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500"}`}>{p.status}</span>
                  </div>
                  <div className="font-semibold">{p.lot_name}</div>
                  <div className="text-xs text-muted-foreground">{p.producer} · {p.format} · base {eur(p.starting_bid)} · stima {eur(p.estimate_min)}-{eur(p.estimate_max)}</div>
                  {p.admin_notes && <div className="text-xs italic text-muted-foreground mt-1 border-l-2 border-[#C9A961] pl-2">{p.admin_notes}</div>}
                </div>
                <div className="text-[10px] text-muted-foreground whitespace-nowrap"><Clock className="inline h-3 w-3" /> {new Date(p.created_at).toLocaleDateString("it-IT")}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <ProposalDecisionModal proposal={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); reload(); }} />}
    </div>
  );
}

export default function AdminAuctions() {
  const [pending, setPending] = useState(0);
  useEffect(() => {
    api.get("/admin/auction-proposals/pending-count").then((r) => setPending(r.data.pending || 0)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-auctions-page" className="container-xl py-8">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-2">Super Admin · Aste</div>
      <h1 className="editorial-heading text-4xl flex items-center gap-3"><Gavel className="h-8 w-8 text-[hsl(var(--primary))]" />Aste Club Privilegia</h1>
      <p className="text-muted-foreground mt-2 mb-6">Gestione completa aste: crea lotti manualmente o approva proposte dei rivenditori B2B.</p>

      <Tabs defaultValue={pending > 0 ? "proposals" : "auctions"} className="mt-6">
        <TabsList>
          <TabsTrigger data-testid="adm-auc-tab-list" value="auctions" className="gap-2"><Gavel className="h-4 w-4" />Aste attive</TabsTrigger>
          <TabsTrigger data-testid="adm-auc-tab-prop" value="proposals" className="gap-2 relative">
            <AlertCircle className="h-4 w-4" />
            Proposte B2B
            {pending > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-red-500 text-white rounded-full">{pending}</span>}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="auctions" className="mt-6"><AuctionsTab /></TabsContent>
        <TabsContent value="proposals" className="mt-6"><ProposalsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
